import { IS_KAKAO_APP, IS_MOBILE } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { Button, Divider, Title, useToast } from '@zenterprise-inc/ui';
import { pascalCase } from 'es-toolkit';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CertType } from '@/__generated__/hometaxSimpleAuthMutation.graphql';
import { openKakaoTalk } from '@/components/tax-refund/common/utils/openKakaoTalk';
import { useRequestSimpleAuthToken } from '@/lib/hooks/use-hometax-simple-auth';
import { simpleAuthCertTypeAtom, simpleAuthCredentialAtom, simpleAuthUserIdentityAtom } from '@/lib/stores/simple-auth';

export const SIMPLE_AUTH_TIMEOUT_TOAST_TITLE = '인증 유효 시간이 지났어요. 재인증 요청을 눌러 다시 진행해주세요.';

export const SimpleAuthConfirmCTA = ({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) => {
  return (
    <Button.StickyBottom.Wrapper className="page-padding">
      <Button.StickyBottom.Button loading={isLoading} state={'primary'} onClick={onClick}>
        인증 완료
      </Button.StickyBottom.Button>
    </Button.StickyBottom.Wrapper>
  );
};

export const SimpleAuthenticationGuide = ({ onClickCheckForm }: { onClickCheckForm: () => void }) => {
  const certType = useAtomValue(simpleAuthCertTypeAtom);

  const title = useMemo(
    () =>
      certType === 'kakao' && IS_KAKAO_APP
        ? '카카오 인증을 완료했다면\n인증 완료 버튼을 눌러주세요'
        : certType === 'kakao'
          ? '지금 메시지를 보냈어요!\n카카오톡 앱에서 확인해주세요.'
          : '지금 메시지를 보냈어요!\n네이버 앱에서 확인해주세요.',
    [certType]
  );

  return (
    <div className="grow">
      <Title.Headline title={title} size={'large'} />
      <Divider.Line />
      <div className={'mt-6 flex flex-col gap-y-3 pb-5'}>
        <p className={'body-14 font-medium text-gray-800'}>인증 메시지를 받지 못하셨다면?</p>
        <ul className={'body-14 text-gray-700 list-disc list-inside flex flex-col gap-y-2'}>
          <li>앱 설치 및 수신차단 여부를 확인해주세요.</li>
          <li>
            입력한{' '}
            <span className={'cursor-pointer text-violet-600 hover:underline'} onClick={onClickCheckForm}>
              간편인증 정보
            </span>
            를 다시 확인해주세요.
          </li>
        </ul>
      </div>
    </div>
  );
};

export const SimpleAuthRetrySection = ({ min, sec, restart, isTimeOut }: { min: string; sec: string; restart: () => void; isTimeOut: boolean }) => {
  const { requestSimpleAuthToken: commit, isLoading: isInFlight } = useRequestSimpleAuthToken();

  const certType = useAtomValue(simpleAuthCertTypeAtom);
  const simpleAuthUserIdentity = useAtomValue(simpleAuthUserIdentityAtom);
  const setSimpleAuthCredential = useSetAtom(simpleAuthCredentialAtom);

  const { pageName, sendClickEvent } = useUserEventLogger();
  const toast = useToast();
  const hasShownTimeoutToastRef = useRef(false);
  const remainSeconds = ((min ? parseInt(min, 10) : 0) || 0) * 60 + ((sec ? parseInt(sec, 10) : 0) || 0);
  const isDisabled = remainSeconds > 270;

  const showErrorToast = useCallback(
    (content: string) => {
      toast({ content, icon: 'error', iconColor: 'red.600' });
    },
    [toast]
  );

  const reRequestHometaxSimpleAuth = () => {
    if (!simpleAuthUserIdentity) return;

    const { name, birthday, phone } = simpleAuthUserIdentity;
    commit({
      variables: {
        certType: pascalCase(certType) as CertType,
        name,
        phone,
        birthday
      },
      onCompleted: (res, errors) => {
        if (errors) {
          showErrorToast('일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.');
          return;
        }
        if (res.hometaxSimpleAuth.errors?.code) {
          showErrorToast(res.hometaxSimpleAuth.errors.message ?? '');
          return;
        }
        const { reqTxId, cxId, token } = res.hometaxSimpleAuth;
        restart();
        if (reqTxId && cxId && token) {
          if (certType === 'kakao') {
            openKakaoTalk(cxId);
          }
          setSimpleAuthCredential({
            reqTxId,
            cxId,
            token
          });
        }
      },
      onError: () => {
        showErrorToast('알 수 없는 오류입니다.');
      }
    });
  };

  const handleRetryClick = () => {
    sendClickEvent(`${pageName}_retry`);
    reRequestHometaxSimpleAuth();
  };

  useEffect(() => {
    if (!isTimeOut) {
      hasShownTimeoutToastRef.current = false;
      return;
    }

    if (hasShownTimeoutToastRef.current) return;

    hasShownTimeoutToastRef.current = true;
    showErrorToast(SIMPLE_AUTH_TIMEOUT_TOAST_TITLE);
  }, [isTimeOut, showErrorToast]);

  return (
    <div className={'mt-16'}>
      <div className="flex items-center justify-center gap-4">
        <p className="text-gray-600">남은 시간 {`${min}:${sec}`}</p>
        <Button.Text loading={isInFlight} disabled={isDisabled} state={'primary'} onClick={handleRetryClick}>
          <span>재인증 요청</span>
          <RefreshIcon />
        </Button.Text>
      </div>
      <div className="relative flex items-center justify-center">
        <img
          src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/refund/images/simple-auth/img-simple-auth-${certType === 'kakao' && IS_MOBILE && IS_KAKAO_APP ? 'kakao-inapp' : certType}.png`}
          alt={'인증'}
          width={339}
          height={234}
          className={'flex flex-none'}
        />
      </div>
    </div>
  );
};

const RefreshIcon = () => {
  return (
    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.8975 3.54702C7.95727 3.16486 9.1186 3.17329 10.1727 3.57079C11.2268 3.9683 12.1045 4.72881 12.6481 5.71557C13.1916 6.70234 13.3653 7.85064 13.1379 8.95402C12.9105 10.0574 12.297 11.0435 11.4076 11.7349C10.5182 12.4264 9.41132 12.7779 8.28594 12.7263C7.16056 12.6746 6.09052 12.2232 5.2682 11.4532C4.44588 10.6832 3.92522 9.64508 3.79984 8.52552C3.76296 8.19621 3.46611 7.95914 3.13679 7.99602C2.80748 8.0329 2.57042 8.32976 2.6073 8.65907C2.76447 10.0625 3.41714 11.3639 4.44798 12.3291C5.47882 13.2944 6.82019 13.8603 8.23093 13.925C9.64167 13.9898 11.0292 13.5491 12.1441 12.6823C13.259 11.8155 14.0282 10.5794 14.3132 9.19623C14.5982 7.81307 14.3805 6.37359 13.6992 5.13661C13.0178 3.89963 11.9175 2.94628 10.5961 2.44798C9.27473 1.94968 7.81892 1.93911 6.49043 2.41817C5.7342 2.69088 5.02989 3.10607 4.43356 3.64556V2.66641C4.43356 2.33504 4.16493 2.06641 3.83356 2.06641C3.50219 2.06641 3.23356 2.33504 3.23356 2.66641V5.31546C3.23204 5.36874 3.23766 5.4218 3.25001 5.47319C3.25728 5.50359 3.26687 5.53308 3.27857 5.56148C3.34516 5.72311 3.48029 5.84927 3.64777 5.90375C3.71212 5.92476 3.77842 5.93429 3.84399 5.93307H6.50023C6.8316 5.93307 7.10023 5.66444 7.10023 5.33307C7.10023 5.0017 6.8316 4.73307 6.50023 4.73307H5.03329C5.54592 4.20847 6.18773 3.80297 6.8975 3.54702Z"
        fill="currentColor"
      />
    </svg>
  );
};
